/**
 * NGK Tours — Service Voucher PDF generator (jsPDF).
 *
 * Reproduces the approved design "Service_Voucher_NRT101_1126_Grand_Mercure.pdf":
 *   • US Letter portrait, 0.5pt page frame
 *   • Page 1 — SERVICE VOUCHER (header, group/pax/hotel row, Accommodation +
 *     Daily Itinerary boxes, disclaimer, Tour Contacts, closing note, office footer)
 *   • Page 2 — SERVICIO GREET & GO (arrival instructions, photos, WhatsApp contacts)
 *
 * All coordinates are PDF points measured from the approved design.
 *
 *   await generateServiceVoucherPdf(data);                       // download
 *   const blob = await generateServiceVoucherPdf(data, { output: 'blob' });
 */
import { jsPDF } from 'jspdf';
import type {
  DateInput,
  ItineraryItem,
  NamedContact,
  RichParagraph,
  ServiceVoucherData,
  ServiceVoucherOptions,
  TextRun,
} from './types';

export type { ServiceVoucherData, ServiceVoucherOptions } from './types';

// ════════════════════════════════════════════════════════════════════
// THEME — values taken from the approved design
// ════════════════════════════════════════════════════════════════════
export const THEME = {
  color: {
    navy: '#1B1F4B', // names, values, rooms panel
    text: '#1A1A2E', // body text
    black: '#000000',
    green: '#2D6A3F', // labels, section titles, dates
    muted: '#7A7A8A', // phones, "Nov 2026"
    blue: '#002060', // footer, closing note, Greet & Go highlights
    beige: '#F0EDE8', // check-in/out boxes, contact cards
    mint: '#F0F8F2', // room counts panel
    rule: '#B8DFC8', // thin green rule under section titles
    boxBorder: '#BFBFBF', // accommodation / itinerary box
    headerLine: '#7F7F7F', // header lines, itinerary separators
    refHighlight: '#D3D3D3', // grey highlight behind REF
    contactsBorder: '#DDDDE8',
    white: '#FFFFFF',
    logoCaption: '#163A28',
  },
  fontSize: 10, // the design uses 10pt almost everywhere
  /** Text above the logo, as in the design. Set '' to hide. */
  logoCaption: 'New Egylook Tours',
  footerLines: ['Office #116, Bayat Complex, Abu Hail, Dubai, UAE', 'Email: info@newegylooktours.com'],
  labels: {
    voucherTitle: 'SERVICE VOUCHER',
    groupName: 'GROUP NAME:',
    total: 'TOTAL:',
    passengers: 'PASSENGERS:',
    hotel: 'HOTEL:',
    accommodation: 'ACCOMMODATION',
    itinerary: 'DAILY ITINERARY',
    itineraryCont: 'DAILY ITINERARY (cont.)',
    hotelPrefix: 'Hotel: ',
    checkIn: 'CHECK-IN',
    checkOut: 'CHECK-OUT',
    totalRooms: 'TOTAL:',
    rooms: 'ROOMS',
    excluded: 'Excluded: ',
    tourContacts: 'TOUR CONTACTS:',
    tourLeader: 'NGK T/L:',
    guide: 'NGK GUÍA:',
    emergency: '24/7 EMERGENCY',
    stayOrdinals: ['First Stay', 'Second Stay', 'Third Stay', 'Fourth Stay', 'Fifth Stay'],
  },
  defaults: {
    disclaimer:
      '-El Orden del Itinerario mostrado es genérico con fines orientativos. Podría alterarse sin afectar a las visitas ni al contenido de las mismas.',
    closingNote: '“que tenga muy buena estancia”',
  },
  empty: '—',
};

/** Default Greet & Go copy (from the design). Override via data.greetAndGo. */
export const GREET_AND_GO_DEFAULTS = {
  title: 'SERVICIO GREET & GO',
  heading: [
    [{ text: 'Instrucciones de llegada – Servicio Greet & Go en el Aeropuerto de Dubái:', bold: true, color: 'green' }],
    [
      { text: '¡Bienvenido a Dubái', bold: true, color: 'green' },
      { text: '!', bold: true, color: 'text' },
    ],
  ] as RichParagraph[],
  bullets: [
    [
      { text: '•Al llegar, puedes conectarte al WiFi gratuito del aeropuerto: DXB Free WiFi ' },
      { text: '(', bold: true },
      { text: 'nombre de red: DXBFREE).', bold: true, color: 'blue' },
    ],
    [
      { text: '•Después de recoger tu equipaje, dirígete al ' },
      { text: 'salón Greet & Go.', bold: true, color: 'blue' },
    ],
    '•Muestra tu código QR al personal del aeropuerto para que te indiquen a tu conductor asignado.',
    [{ text: '•Si necesitas cualquier ayuda, puedes contactar por WhatsApp con:', underline: true }],
  ] as RichParagraph[],
  contactsLabel: 'NGK T/L',
  closing: '¡Te deseamos una estancia agradable y una experiencia inolvidable!',
};

// ════════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════════
type RGB = [number, number, number];
type Style = 'normal' | 'bold' | 'italic' | 'bolditalic';

const EMPTY = THEME.empty;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FS = THEME.fontSize;
const LINE = 11.3; // design line height for 10pt Georgia

function hex(h: string): RGB {
  const n = h.replace('#', '');
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}
const COL = Object.fromEntries(Object.entries(THEME.color).map(([k, v]) => [k, hex(v)])) as Record<
  keyof typeof THEME.color,
  RGB
>;

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}
export function dash(v: unknown): string {
  return isBlank(v) ? EMPTY : String(v).trim();
}
function num(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function pad2(n: number | null): string {
  return n === null ? EMPTY : String(n).padStart(2, '0');
}

function dateParts(v: DateInput): { y: number; m: number; d: number } | null {
  if (v instanceof Date && !Number.isNaN(v.getTime())) return { y: v.getFullYear(), m: v.getMonth() + 1, d: v.getDate() };
  if (typeof v === 'string') {
    const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  }
  return null;
}
/** "2026-10-24" → "24 OCT 2026" */
function fmtHeaderDate(v: DateInput): string {
  const p = dateParts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m - 1].toUpperCase()} ${p.y}` : dash(v);
}
/** "2026-11-06" → "06/11" */
function fmtShortDate(v: DateInput): string {
  const p = dateParts(v);
  return p ? `${pad2(p.d)}/${pad2(p.m)}` : dash(v);
}
/** "2026-11-06" → { day: "06", rest: "Nov 2026" } */
function fmtStayDate(v: DateInput): { day: string; rest: string } {
  const p = dateParts(v);
  if (p) return { day: pad2(p.d), rest: `${MONTHS[p.m - 1]} ${p.y}` };
  if (isBlank(v)) return { day: EMPTY, rest: '' };
  return { day: String(v), rest: '' };
}

/** Service-Voucher-{code|ref|bookingNumber}.pdf */
export function getServiceVoucherFileName(data: ServiceVoucherData): string {
  const id = (data.code || data.ref || data.bookingNumber || 'draft').toString();
  const safe = id.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'draft';
  return `Service-Voucher-${safe}.pdf`;
}

// ════════════════════════════════════════════════════════════════════
// Assets
// ════════════════════════════════════════════════════════════════════
export interface LoadedImage {
  dataUrl: string;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
}
export interface FontSet {
  family: string;
  normal: string; // base64 TTF
  bold: string;
  italic: string;
  bolditalic: string;
}
export interface VoucherAssets {
  logo?: LoadedImage | null;
  font?: FontSet | null;
  phoneIcon?: LoadedImage | null;
  wifi?: LoadedImage | null;
  lounge?: LoadedImage | null;
  qr?: LoadedImage | null;
}

function publicUrl(): string {
  try {
    return process.env.PUBLIC_URL || '';
  } catch {
    return '';
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
function htmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

/** Loads PNG/JPEG/SVG into a data URL for jsPDF. SVG → PNG (4×), alpha kept. Returns null on failure. */
export async function loadImage(url: string): Promise<LoadedImage | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const isSvg = blob.type.includes('svg') || /\.svg(\?|$)/i.test(url);
    if (!isSvg && !blob.type.startsWith('image/')) throw new Error(`not an image (${blob.type})`);
    const src = await blobToDataUrl(blob);
    const img = await htmlImage(src);
    const w = img.naturalWidth || 1;
    const h = img.naturalHeight || 1;
    if (src.startsWith('data:image/png')) return { dataUrl: src, width: w, height: h, format: 'PNG' };
    if (src.startsWith('data:image/jpeg')) return { dataUrl: src, width: w, height: h, format: 'JPEG' };
    const scale = isSvg ? 4 : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height, format: 'PNG' };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[ServiceVoucher] could not load ${url}`, err);
    return null;
  }
}

async function fetchBase64(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  const type = res.headers.get('content-type') || '';
  if (type.includes('text/html')) throw new Error(`${url} returned HTML (file missing?)`);
  const buf = new Uint8Array(await res.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + 0x8000)));
  return btoa(bin);
}

type FontFiles = NonNullable<ServiceVoucherOptions['fontFiles']>;

/**
 * Font files tried in order. Georgia is the font of the approved design
 * (copy georgia.ttf / georgiab.ttf / georgiai.ttf / georgiaz.ttf from C:\Windows\Fonts
 * into public/fonts). Gelasio (Georgia-metric, open licence) is the fallback.
 */
export function defaultFontCandidates(base = `${publicUrl()}/fonts`): FontFiles[] {
  return [
    {
      family: 'Georgia',
      regular: `${base}/georgia.ttf`,
      bold: `${base}/georgiab.ttf`,
      italic: `${base}/georgiai.ttf`,
      boldItalic: `${base}/georgiaz.ttf`,
    },
    {
      family: 'Gelasio',
      regular: `${base}/Gelasio-Regular.ttf`,
      bold: `${base}/Gelasio-Bold.ttf`,
      italic: `${base}/Gelasio-Italic.ttf`,
      boldItalic: `${base}/Gelasio-BoldItalic.ttf`,
    },
  ];
}

async function loadFontFiles(f: FontFiles): Promise<FontSet> {
  const [normal, bold, italic, bolditalic] = await Promise.all([
    fetchBase64(f.regular),
    fetchBase64(f.bold),
    fetchBase64(f.italic),
    fetchBase64(f.boldItalic),
  ]);
  return { family: f.family, normal, bold, italic, bolditalic };
}

let fontCache: Promise<FontSet | null> | null = null;
/** Loads the first font family whose 4 files are all available; null → Times. Cached per page load. */
export function loadFonts(opts: ServiceVoucherOptions = {}): Promise<FontSet | null> {
  const custom = !!(opts.fontFiles || opts.fontBaseUrl);
  if (fontCache && !custom) return fontCache;
  const candidates = opts.fontFiles ? [opts.fontFiles] : defaultFontCandidates(opts.fontBaseUrl);
  const p = (async () => {
    for (const c of candidates) {
      try {
        return await loadFontFiles(c);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[ServiceVoucher] ${c.family} font not loaded, trying next`, err);
      }
    }
    // eslint-disable-next-line no-console
    console.warn('[ServiceVoucher] no font files found, using Times');
    if (!custom) fontCache = null;
    return null;
  })();
  if (!custom) fontCache = p;
  return p;
}

export async function loadVoucherAssets(opts: ServiceVoucherOptions = {}): Promise<VoucherAssets> {
  const img = opts.imageBaseUrl || `${publicUrl()}/images`;
  const [logo, font, phoneIcon, wifi, lounge, qr] = await Promise.all([
    loadImage(opts.logoUrl || `${img}/ngk-logo.png`),
    loadFonts(opts),
    loadImage(`${img}/icons/phone.png`),
    loadImage(`${img}/greet-go/dxb-wifi.jpg`),
    loadImage(`${img}/greet-go/greet-go-lounge.jpg`),
    loadImage(`${img}/greet-go/qr-phone.jpg`),
  ]);
  return { logo, font, phoneIcon, wifi, lounge, qr };
}

// ════════════════════════════════════════════════════════════════════
// Drawing primitives
// ════════════════════════════════════════════════════════════════════
interface Ctx {
  doc: jsPDF;
  family: string;
  assets: VoucherAssets;
}

function font(ctx: Ctx, style: Style, color: RGB, size = FS) {
  ctx.doc.setFont(ctx.family, style);
  ctx.doc.setFontSize(size);
  ctx.doc.setTextColor(...color);
}
function width(ctx: Ctx, s: string, style: Style, size = FS): number {
  ctx.doc.setFont(ctx.family, style);
  ctx.doc.setFontSize(size);
  return ctx.doc.getTextWidth(s);
}
function text(
  ctx: Ctx,
  s: string,
  x: number,
  y: number,
  style: Style,
  color: RGB,
  align: 'left' | 'center' | 'right' = 'left',
  size = FS,
) {
  font(ctx, style, color, size);
  ctx.doc.text(s, x, y, { align, baseline: 'alphabetic' });
}
function wrap(ctx: Ctx, s: string, maxW: number, style: Style, size = FS): string[] {
  ctx.doc.setFont(ctx.family, style);
  ctx.doc.setFontSize(size);
  return ctx.doc.splitTextToSize(s, maxW) as string[];
}
function fillRect(ctx: Ctx, x: number, y: number, w: number, h: number, color: RGB) {
  ctx.doc.setFillColor(...color);
  ctx.doc.rect(x, y, w, h, 'F');
}

function drawPageFrame(ctx: Ctx) {
  ctx.doc.setDrawColor(0, 0, 0);
  ctx.doc.setLineWidth(0.5);
  ctx.doc.rect(24.25, 24.25, 563.6, 743.6, 'S');
}

/** Logo band between two grey lines. `dy` = vertical shift (page 2 sits 4pt higher). */
function drawHeaderBand(ctx: Ctx, dy: number) {
  fillRect(ctx, 36, 43.4 + dy, 548.8, 0.5, COL.headerLine);
  fillRect(ctx, 35.3, 152.4 + dy, 549.5, 0.5, COL.headerLine);

  if (THEME.logoCaption) {
    // Caption sized to the design's measured width (119.6pt), centered over the wordmark.
    text(ctx, THEME.logoCaption, 183.6, 75.4 + dy, 'italic', COL.logoCaption, 'center', 12.5);
  }

  const logo = ctx.assets.logo;
  // Area of the NGK artwork in the design (centre x 158.6). Aspect ratio is preserved.
  const box = { cx: 158.6, y: 80.5 + dy, w: 176, h: 62 };
  if (logo) {
    const ratio = logo.width / logo.height;
    let w = box.w;
    let h = w / ratio;
    if (h > box.h) {
      h = box.h;
      w = h * ratio;
    }
    ctx.doc.addImage(logo.dataUrl, logo.format, box.cx - w / 2, box.y, w, h, 'ngk-logo', 'FAST');
  } else {
    text(ctx, 'NGK', box.cx, box.y + 44, 'bold', COL.navy, 'center', 54);
  }
}

/** Thin "→" (the design uses Arial's arrow, which Georgia-style fonts lack). */
function drawArrow(ctx: Ctx, x0: number, x1: number, y: number) {
  const { doc } = ctx;
  doc.setDrawColor(...COL.muted);
  doc.setLineWidth(0.6);
  doc.line(x0, y, x1 - 0.6, y);
  doc.setFillColor(...COL.muted);
  doc.triangle(x1, y, x1 - 3.4, y - 2.1, x1 - 3.4, y + 2.1, 'F');
}

function drawFooter(ctx: Ctx) {
  THEME.footerLines.forEach((line, i) => text(ctx, line, 36, 743.1 + i * 11.4, 'normal', COL.blue));
}

/** Rich text with word wrap across runs. Returns the baseline of the last line. */
function richParagraph(ctx: Ctx, para: RichParagraph, x: number, y: number, maxW: number, lineH = 11.4): number {
  const runs: TextRun[] = typeof para === 'string' ? [{ text: para }] : para;
  type Piece = { t: string; run: TextRun; w: number };
  type Token = { pieces: Piece[]; w: number; space: boolean };
  // Split into words; a word may span several runs (e.g. "(" + "nombre"), so it never breaks mid-word.
  const tokens: Token[] = [];
  runs.forEach((run) => {
    const style: Style = run.bold ? 'bold' : 'normal';
    run.text.split(/(\s+)/).forEach((t) => {
      if (!t) return;
      const space = /^\s+$/.test(t);
      const piece = { t, run, w: width(ctx, t, style) };
      const prev = tokens[tokens.length - 1];
      if (!space && prev && !prev.space) {
        prev.pieces.push(piece);
        prev.w += piece.w;
      } else tokens.push({ pieces: [piece], w: piece.w, space });
    });
  });
  const lines: Token[][] = [[]];
  let lw = 0;
  tokens.forEach((tk) => {
    const line = lines[lines.length - 1];
    if (!tk.space && lw + tk.w > maxW && line.some((q) => !q.space)) {
      while (line.length && line[line.length - 1].space) line.pop();
      lines.push([tk]);
      lw = tk.w;
    } else if (!(tk.space && line.length === 0)) {
      line.push(tk);
      lw += tk.w;
    }
  });
  let by = y;
  lines.forEach((line, li) => {
    by = y + li * lineH;
    let cx = x;
    line.forEach((tk) =>
      tk.pieces.forEach((p) => {
        const color = COL[p.run.color || 'text'];
        text(ctx, p.t, cx, by, p.run.bold ? 'bold' : 'normal', color);
        if (p.run.underline) fillRect(ctx, cx, by + 0.8, p.w, 0.5, color);
        cx += p.w;
      }),
    );
  });
  return by;
}

// ════════════════════════════════════════════════════════════════════
// Page 1 — SERVICE VOUCHER
// ════════════════════════════════════════════════════════════════════
const BOX_TOP = 224.2;
const BOX_MIN_BOTTOM = 544.4;
const BOX_MAX_BOTTOM = 566; // beyond this the contacts/closing would hit the footer
const LEFT_X1 = 242.7; // accommodation cell right edge
const RIGHT_X1 = 580.4; // itinerary cell right edge

function refText(d: ServiceVoucherData): string {
  return dash(d.ref || d.code || d.bookingNumber);
}

function drawVoucherHeader(ctx: Ctx, d: ServiceVoucherData) {
  drawHeaderBand(ctx, 0);
  const R = 579.3;
  text(ctx, THEME.labels.voucherTitle, R, 67.6, 'bold', COL.black, 'right');

  const refVal = ` ${refText(d)}`;
  const wRef = width(ctx, 'REF:', 'bold');
  const wVal = width(ctx, refVal, 'bold');
  fillRect(ctx, R - wRef - wVal - 0.3, 72.7, wRef + wVal + 0.5, 11.4, COL.refHighlight);
  text(ctx, 'REF:', R - wVal - wRef, 82.0, 'bold', COL.black);
  text(ctx, refVal, R - wVal, 82.0, 'bold', COL.navy);

  text(ctx, `Date: ${fmtHeaderDate(d.voucherDate)}`, R, 96.3, 'bold', COL.black, 'right');
}

function paxTotal(d: ServiceVoucherData): number | null {
  const a = num(d.adults);
  const c = num(d.children);
  const i = num(d.infants);
  return num(d.paxTotal) ?? (a !== null || c !== null || i !== null ? (a ?? 0) + (c ?? 0) + (i ?? 0) : null);
}

function drawInfoRow(ctx: Ctx, d: ServiceVoucherData) {
  const L = THEME.labels;
  // GROUP NAME
  text(ctx, L.groupName, 41.4, 175.5, 'bold', COL.black);
  fillRect(ctx, 41.4, 176.3, width(ctx, L.groupName, 'bold'), 0.6, COL.black);
  wrap(ctx, dash(d.groupName || d.guestName), 176, 'bold')
    .slice(0, 4)
    .forEach((ln, i) => text(ctx, ln, 41.4, 188.9 + i * LINE, 'bold', COL.text));

  // TOTAL PASSENGERS
  const pax = paxTotal(d);
  text(ctx, L.total, 268.8, 175.5, 'bold', COL.black);
  fillRect(ctx, 266.3, 176.3, width(ctx, L.total, 'bold') + 2.5, 0.6, COL.black);
  text(ctx, L.passengers, 257.9, 188.8, 'bold', COL.black);
  text(ctx, pax === null ? EMPTY : `${pax} Pax`, 270.4, 202.1, 'bold', COL.black);

  // HOTEL
  text(ctx, L.hotel, 463.5, 175.5, 'bold', COL.black);
  fillRect(ctx, 463.5, 176.3, width(ctx, L.hotel, 'bold'), 0.6, COL.black);
  wrap(ctx, dash(d.hotelName).toUpperCase(), 165, 'bold')
    .slice(0, 3)
    .forEach((ln, i) => text(ctx, ln, 486, 188.9 + i * LINE, 'bold', COL.black, 'center'));
}

/** Accommodation column. Returns content bottom. `compact` shrinks gaps (many stays). */
function drawAccommodation(ctx: Ctx, d: ServiceVoucherData, compact: boolean, draw: boolean): number {
  const L = THEME.labels;
  if (draw) {
    text(ctx, L.accommodation, 46.7, 233.8, 'normal', COL.green);
    fillRect(ctx, 40.2, 235.9, 198.8, 0.25, COL.rule);
  }

  // Hotel line
  const prefixW = width(ctx, L.hotelPrefix, 'normal');
  const hotelLines = wrap(ctx, dash(d.hotelName), LEFT_X1 - 41.6 - prefixW - 3, 'bold');
  if (draw) {
    text(ctx, L.hotelPrefix, 41.6, 261.8, 'normal', COL.navy);
    hotelLines.forEach((ln, i) => text(ctx, ln, 41.6 + (i === 0 ? prefixW : 0), 261.8 + i * LINE, 'bold', COL.navy));
  }
  let y = 261.8 + (hotelLines.length - 1) * LINE + 12.8; // first stay label baseline

  // Stays
  const stays = d.stays && d.stays.length ? d.stays : [{ label: null, checkIn: d.checkIn, checkOut: d.checkOut }];
  const boxH = compact ? 46 : 56.2;
  const k = boxH / 56.9;
  const afterBox = compact ? 6 : 9;
  stays.forEach((s, i) => {
    const label = !isBlank(s.label) ? String(s.label) : stays.length > 1 ? L.stayOrdinals[i] || `Stay ${i + 1}` : '';
    if (draw && label) text(ctx, label, 41.6, y, 'bold', COL.navy);
    const top = label ? y + 3.7 : y - 8;
    if (draw) {
      fillRect(ctx, 41.6, top, 70, boxH, COL.beige);
      fillRect(ctx, 128.7, top, 114, boxH, COL.beige);
      [
        { cx: 76.6, label: L.checkIn, v: fmtStayDate(s.checkIn) },
        { cx: 185.7, label: L.checkOut, v: fmtStayDate(s.checkOut) },
      ].forEach((c) => {
        text(ctx, c.label, c.cx, top + 14.3 * k, 'normal', COL.green, 'center');
        text(ctx, c.v.day, c.cx, top + 28 * k, 'bold', COL.navy, 'center');
        if (c.v.rest) text(ctx, c.v.rest, c.cx, top + 40.9 * k, 'bold', COL.muted, 'center');
      });
      drawArrow(ctx, 117.2, 126.2, top + 33.6 * k);
    }
    y = top + boxH + afterBox;
  });
  const lastBoxBottom = y - afterBox;

  // Rooms panel
  const rb = d.roomBreakdown || {};
  const twn = num(rb.twn);
  const dbl = num(rb.dbl);
  const sgl = num(rb.sgl);
  const total =
    num(rb.total) ?? (twn !== null || dbl !== null || sgl !== null ? (twn ?? 0) + (dbl ?? 0) + (sgl ?? 0) : null);
  const rTop = lastBoxBottom + (compact ? 12 : 32.8);
  const rH = 51.3;
  if (draw) {
    fillRect(ctx, 41.6, rTop, 66.1, rH, COL.navy);
    fillRect(ctx, 107.7, rTop, LEFT_X1 - 107.7, rH, COL.mint);
    text(ctx, `${L.totalRooms} ${total === null ? EMPTY : total}`, 46.7, rTop + 12.1, 'normal', COL.white);
    text(ctx, L.rooms, 46.7, rTop + 23.5, 'normal', COL.white);
    const cols: Array<[number, number, string, string]> = [
      [111.6, 111.6, pad2(twn), 'TWN'],
      [151.0, 147.6, pad2(dbl), 'DBL'],
      [189.5, 186.5, pad2(sgl), 'SGL'],
    ];
    cols.forEach(([nx, lx, n, lab]) => {
      text(ctx, n, nx, rTop + 12.1, 'bold', COL.green);
      text(ctx, lab, lx, rTop + 23.5, 'bold', COL.green);
    });
  }
  let bottom = rTop + rH;

  // Excluded
  if (!isBlank(d.exclusions)) {
    const ex = String(d.exclusions).trim();
    const full = /^excluded/i.test(ex) ? ex : `${L.excluded}${ex}`;
    const lines = wrap(ctx, full, 199, 'bold');
    const first = bottom + (compact ? 18 : 32);
    if (draw) lines.forEach((ln, i) => text(ctx, ln, 41.6, first + i * LINE, 'bold', COL.black));
    bottom = first + (lines.length - 1) * LINE + 13.6;
  }
  return bottom;
}

interface ItinRow {
  header?: string; // group header drawn above this row
  stay: string | null;
  item: ItineraryItem;
  titleLines: string[];
  detailLines: string[];
}

function buildRows(ctx: Ctx, items: ItineraryItem[]): ItinRow[] {
  const textW = RIGHT_X1 - 333.6 - 4;
  let prev: string | null = null;
  return items.map((it, i) => {
    const stay = isBlank(it.stay) ? null : String(it.stay).trim();
    const header = stay && (i === 0 || stay !== prev) ? stay : undefined;
    prev = stay;
    const titleLines = isBlank(it.title) ? [] : wrap(ctx, String(it.title), textW, 'bold');
    const detailLines = isBlank(it.details)
      ? []
      : String(it.details)
          .split(/\r?\n/)
          .flatMap((p) => wrap(ctx, p, textW, 'bold'));
    if (!titleLines.length && !detailLines.length) titleLines.push(EMPTY);
    return { header, stay, item: it, titleLines, detailLines };
  });
}

/** Measures (and optionally draws) itinerary rows from `startY`. */
function layoutItinerary(
  ctx: Ctx,
  rows: ItinRow[],
  startY: number,
  maxBottom: number,
  compact: boolean,
  draw: boolean,
): { bottom: number; count: number } {
  const rowMin = compact ? 0 : 42;
  const rowPad = compact ? 10 : 22;
  const groupGap = compact ? 4 : 7;
  let y = startY;
  let count = 0;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const needsHeader = r.header !== undefined || count === 0;
    const headerTop = needsHeader && count > 0 ? y + groupGap : y;
    const lineY = needsHeader ? headerTop + 18.2 : y;
    const first = lineY + 9.7;
    const n = r.titleLines.length;
    const lastBase = first + (n - 1) * LINE + (r.detailLines.length ? 13.3 + (r.detailLines.length - 1) * LINE : 0);
    const rowBottom = lineY + Math.max(rowMin, lastBase - lineY + rowPad);
    if (rowBottom > maxBottom && count > 0) break;

    if (draw) {
      if (needsHeader) {
        if (r.header) text(ctx, r.header, 274.5, headerTop + 14.4, 'normal', COL.navy);
        fillRect(ctx, 260.1, lineY, RIGHT_X1 - 260.1, 0.5, COL.headerLine);
      }
      text(ctx, fmtShortDate(r.item.date), 265.5, first, 'bold', COL.green);
      ctx.doc.setFillColor(...COL.green);
      ctx.doc.circle(303.5, first - 3.3, 1.9, 'F');
      r.titleLines.forEach((ln, j) => text(ctx, ln, 333.6, first + j * LINE, 'bold', COL.text));
      const dFirst = first + (n - 1) * LINE + 13.3;
      r.detailLines.forEach((ln, j) => text(ctx, ln, 333.6, dFirst + j * LINE, 'bold', COL.black));
      // separator under the row, unless the next row opens a new group (its header rule follows)
      const next = rows[i + 1];
      if (!next || next.header === undefined) fillRect(ctx, 260.1, rowBottom, RIGHT_X1 - 260.1, 0.5, COL.headerLine);
    }
    y = rowBottom;
    count++;
  }
  return { bottom: y, count };
}

function drawBoxFrame(ctx: Ctx, top: number, bottom: number) {
  const c = COL.boxBorder;
  fillRect(ctx, 36, top, 544.9, 0.5, c);
  fillRect(ctx, 36, bottom, 544.9, 0.5, c);
  [36, LEFT_X1, 254.6, RIGHT_X1].forEach((x) => fillRect(ctx, x, top, 0.5, bottom - top, c));
}

function drawContactsAndClosing(ctx: Ctx, d: ServiceVoucherData, boxBottom: number) {
  const L = THEME.labels;
  // Disclaimer
  let y = boxBottom + 8.8;
  const disclaimer = d.disclaimer === undefined || d.disclaimer === null ? THEME.defaults.disclaimer : d.disclaimer;
  if (!isBlank(disclaimer)) {
    const lines = wrap(ctx, String(disclaimer), 540, 'bold', 9);
    lines.forEach((ln, i) => text(ctx, ln, 36, y + i * 10.1, 'bold', COL.text, 'left', 9));
    y += (lines.length - 1) * 10.1 + 10;
  } else {
    y += 10.1;
  }

  // Tour contacts panel
  const T = y;
  const c = d.contacts || {};
  const emergency = (c.emergency || []).filter((e) => e && (!isBlank(e.name) || !isBlank(e.phone)));
  // Two per row (second column at x 429.8, as in the design) when both fit inside the box;
  // otherwise one per row. Long single entries are truncated to the box width.
  const EM_X0 = 267.5;
  const EM_X1 = 429.8;
  const EM_RIGHT = 572;
  const emLabel = (e: NamedContact) =>
    [isBlank(e.name) ? '' : `${String(e.name).trim()}:`, dash(e.phone)].filter(Boolean).join('  ');
  const emPlaced: Array<{ s: string; x: number; row: number }> = [];
  let emRow = 0;
  for (let i = 0; i < emergency.length; i++) {
    const s0 = emLabel(emergency[i]);
    const w0 = width(ctx, s0, 'bold');
    const next = emergency[i + 1];
    if (next) {
      const s1 = emLabel(next);
      const x1 = Math.max(EM_X1, EM_X0 + w0 + 14);
      if (x1 + width(ctx, s1, 'bold') <= EM_RIGHT) {
        emPlaced.push({ s: s0, x: EM_X0, row: emRow }, { s: s1, x: x1, row: emRow });
        emRow++;
        i++;
        continue;
      }
    }
    emPlaced.push({ s: wrap(ctx, s0, EM_RIGHT - EM_X0, 'bold')[0] || s0, x: EM_X0, row: emRow });
    emRow++;
  }
  const extra = (Math.max(1, emRow) - 1) * 14;
  const H = 99.3 + extra;
  ctx.doc.setDrawColor(...COL.contactsBorder);
  ctx.doc.setLineWidth(0.15);
  ctx.doc.rect(31.35, T, 549.25, H, 'S');
  text(ctx, L.tourContacts, 48.6, T + 17.3, 'bold', COL.green);
  fillRect(ctx, 42.1, T + 19.5, 528, 0.25, COL.rule);

  const C = T + 36.2;
  const card = (x: number, w: number, label: string, p?: NamedContact | null) => {
    fillRect(ctx, x, C, w, 54.1, COL.beige);
    fillRect(ctx, x, C, 0.7, 54.1, COL.navy);
    text(ctx, label, x + 8.2, C + 15.2, 'bold', COL.green);
    text(ctx, wrap(ctx, dash(p?.name), w - 12, 'bold')[0] || EMPTY, x + 8.2, C + 29.5, 'bold', COL.navy);
    if (p && !isBlank(p.phone)) text(ctx, String(p.phone), x + 8.2, C + 43.4, 'normal', COL.muted);
  };
  card(43.6, 115.8, L.tourLeader, c.tourLeader);
  card(170.1, 95.5, L.guide, c.guide);

  // 24/7 emergency box — drawn on top of the guide card's right edge, as in the design
  ctx.doc.setFillColor(...COL.white);
  ctx.doc.setDrawColor(0, 0, 0);
  ctx.doc.setLineWidth(0.5);
  ctx.doc.rect(260.0, T + 21.0, 317.6, 75.4 + extra, 'FD');
  text(ctx, L.emergency, 267.5, T + 34.2, 'bold', COL.green);
  if (!emergency.length) text(ctx, EMPTY, 267.5, T + 62.9, 'bold', COL.navy);
  emPlaced.forEach((e) => text(ctx, e.s, e.x, T + 62.9 + e.row * 14, 'bold', COL.navy));

  // Closing note
  const note = d.closingNote === undefined || d.closingNote === null ? THEME.defaults.closingNote : d.closingNote;
  if (!isBlank(note)) text(ctx, String(note), 319.6, T + H + 28.5, 'bolditalic', COL.blue, 'center');
}

/** Draws page 1. Returns itinerary rows that did not fit. */
function drawVoucherPage(ctx: Ctx, d: ServiceVoucherData): ItinRow[] {
  drawPageFrame(ctx);
  drawVoucherHeader(ctx, d);
  drawInfoRow(ctx, d);

  const compactLeft = drawAccommodation(ctx, d, false, false) > BOX_MAX_BOTTOM;
  const leftBottom = drawAccommodation(ctx, d, compactLeft, false);

  const rows = buildRows(ctx, d.itinerary || []);
  let compactRight = false;
  let fit = layoutItinerary(ctx, rows, 235.9, BOX_MAX_BOTTOM - 6, false, false);
  if (fit.count < rows.length) {
    compactRight = true;
    fit = layoutItinerary(ctx, rows, 235.9, BOX_MAX_BOTTOM - 6, true, false);
  }
  const boxBottom = Math.max(BOX_MIN_BOTTOM, leftBottom, fit.bottom + 6);

  text(ctx, THEME.labels.itinerary, 265.1, 233.8, 'normal', COL.green);
  fillRect(ctx, 258.6, 235.9, 318, 0.25, COL.rule);
  if (rows.length) layoutItinerary(ctx, rows.slice(0, fit.count), 235.9, BOX_MAX_BOTTOM - 6, compactRight, true);
  else text(ctx, EMPTY, 265.5, 263.8, 'bold', COL.green);

  drawAccommodation(ctx, d, compactLeft, true);
  drawBoxFrame(ctx, BOX_TOP, boxBottom);
  drawContactsAndClosing(ctx, d, boxBottom);
  drawFooter(ctx);
  return rows.slice(fit.count);
}

/** Extra page(s) when the itinerary does not fit on page 1. */
function drawItineraryContinuation(ctx: Ctx, d: ServiceVoucherData, rows: ItinRow[]) {
  let rest = rows;
  while (rest.length) {
    ctx.doc.addPage('letter', 'portrait');
    drawPageFrame(ctx);
    drawVoucherHeader(ctx, d);
    const top = 170;
    text(ctx, THEME.labels.itineraryCont, 265.1, top + 9.6, 'normal', COL.green);
    fillRect(ctx, 258.6, top + 11.7, 318, 0.25, COL.rule);
    // Repeat the stay header on the new page when a group continues across pages.
    const chunk = rest[0].header === undefined && rest[0].stay ? [{ ...rest[0], header: rest[0].stay }, ...rest.slice(1)] : rest;
    const res = layoutItinerary(ctx, chunk, top + 11.7, 715, true, true);
    const bottom = res.bottom + 6;
    const c = COL.boxBorder;
    fillRect(ctx, 254.6, top, 326.3, 0.5, c);
    fillRect(ctx, 254.6, bottom, 326.3, 0.5, c);
    fillRect(ctx, 254.6, top, 0.5, bottom - top, c);
    fillRect(ctx, RIGHT_X1, top, 0.5, bottom - top, c);
    drawFooter(ctx);
    rest = rest.slice(Math.max(1, res.count));
  }
}

// ════════════════════════════════════════════════════════════════════
// Page 2 — SERVICIO GREET & GO
// ════════════════════════════════════════════════════════════════════
function drawGreetAndGo(ctx: Ctx, d: ServiceVoucherData) {
  const g = d.greetAndGo || {};
  const G = GREET_AND_GO_DEFAULTS;
  const { doc } = ctx;
  doc.addPage('letter', 'portrait');
  drawPageFrame(ctx);
  drawHeaderBand(ctx, -4);
  text(ctx, isBlank(g.title) ? G.title : String(g.title), 399.5, 86.3, 'bold', COL.black);

  // Photos (fixed positions from the design)
  if (g.showImages !== false) {
    const put = (img: LoadedImage | null | undefined, alias: string, x: number, y: number, w: number, h: number) => {
      if (img) doc.addImage(img.dataUrl, img.format, x, y, w, h, alias, 'FAST');
    };
    put(ctx.assets.wifi, 'gg-wifi', 443.5, 197.5, 133.9, 69.8);
    put(ctx.assets.lounge, 'gg-lounge', 452.2, 276.8, 123.0, 152.4);
    put(ctx.assets.qr, 'gg-qr', 311.1, 369.2, 107.1, 74.3);
  }

  // Heading
  let y = 182.9;
  const heading = g.heading && g.heading.length ? g.heading : G.heading;
  heading.forEach((p) => {
    const runs: TextRun[] = typeof p === 'string' ? [{ text: p, bold: true, color: 'green' }] : p;
    y = richParagraph(ctx, runs, 36, y, 540, LINE) + LINE;
  });

  // Bullets on the design's vertical rhythm (space reserved beside the photos)
  const slots = [228.3, 296.5, 364.7, 455.6];
  const bullets = g.bullets && g.bullets.length ? g.bullets : G.bullets;
  let last = y - LINE;
  bullets.forEach((b, i) => {
    const slot = i < slots.length ? slots[i] : last + 34;
    const by = Math.max(slot, last + LINE + 12);
    last = richParagraph(ctx, b, 36, by, 384, 11.4);
  });

  // WhatsApp contacts
  const cy = last + 24.4;
  if (ctx.assets.phoneIcon) doc.addImage(ctx.assets.phoneIcon.dataUrl, 'PNG', 36.8, cy - 9.2, 11.4, 11.4, 'gg-phone', 'FAST');
  text(ctx, isBlank(g.contactsLabel) ? G.contactsLabel : String(g.contactsLabel), 51.4, cy, 'bold', COL.green);
  const c = d.contacts || {};
  const list = (
    g.whatsappContacts && g.whatsappContacts.length ? g.whatsappContacts : [c.tourLeader, ...(c.emergency || [])]
  ).filter((p): p is NamedContact => !!p && (!isBlank(p.name) || !isBlank(p.phone)));
  let ly = cy;
  list.forEach((p, i) => {
    ly = cy + 11.6 + i * 11.4;
    const label = `${dash(p.name)}:`;
    text(ctx, label, 38.4, ly, 'bold', COL.navy);
    text(ctx, dash(p.phone), 38.4 + width(ctx, label, 'bold') + 3.5, ly, 'normal', COL.muted);
  });

  // Closing
  text(ctx, isBlank(g.closing) ? G.closing : String(g.closing), 36, ly + 34.1, 'bold', COL.text);
  drawFooter(ctx);
}

// ════════════════════════════════════════════════════════════════════
// Public API
// ════════════════════════════════════════════════════════════════════
/** Synchronous builder — returns the jsPDF instance (tests, previews, uploads). */
export function buildServiceVoucherDoc(data: ServiceVoucherData, assets: VoucherAssets = {}): jsPDF {
  const d = data || {};
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter', compress: true });

  let family = 'times';
  if (assets.font) {
    const f = assets.font;
    (['normal', 'bold', 'italic', 'bolditalic'] as Style[]).forEach((st) => {
      const file = `${f.family}-${st}.ttf`;
      doc.addFileToVFS(file, f[st]);
      doc.addFont(file, f.family, st);
    });
    family = f.family;
  }
  const ctx: Ctx = { doc, family, assets };

  doc.setProperties({
    title: `Service Voucher ${refText(d)}`,
    subject: 'Service Voucher',
    author: 'NGK Tours',
    creator: 'NGK Tours Portal',
  });

  const overflow = drawVoucherPage(ctx, d);
  if (overflow.length) drawItineraryContinuation(ctx, d, overflow);
  if (d.greetAndGo && d.greetAndGo.enabled !== false) drawGreetAndGo(ctx, d);
  return doc;
}

/**
 * Loads logo, fonts and images from /public, builds the voucher, then downloads it
 * (default), returns a Blob (`output: 'blob'`), or opens it in a new tab (`output: 'open'`).
 */
export async function generateServiceVoucherPdf(
  data: ServiceVoucherData,
  options: ServiceVoucherOptions = {},
): Promise<Blob | void> {
  const assets = await loadVoucherAssets(options);
  const doc = buildServiceVoucherDoc(data, assets);
  const fileName = options.fileName || getServiceVoucherFileName(data);
  if (options.output === 'blob') return doc.output('blob');
  if (options.output === 'open') {
    window.open(String(doc.output('bloburl')), '_blank', 'noopener');
    return;
  }
  doc.save(fileName);
}

export default generateServiceVoucherPdf;
